/* ==========================================================================
   Enova — tarjetas de producto
   1. Miniaturas de variantes: muestra tantas como quepan y resume el resto en "+N".
   2. Alineación por fila: iguala el inicio y la altura de los títulos de las
      tarjetas de una misma fila para que los precios queden a la misma altura.
      Se calcula fila a fila, no con el título más alto de toda la página.
   3. Vídeo de la galería al pasar el ratón.
   4. Colores del modelo: vista previa en la tarjeta y selección de variante en la ficha.
   5. Tarjeta editorial (.enova-card): hasta 4 colores, nombre del color que sigue a la muestra
      y marca de la primera tarjeta de cada fila (sin divisor a la izquierda).
   ========================================================================== */
(() => {
  const CARD = '.m-product-card--style-1';
  const ROW_CONTAINER = '.m-collection-products, .m\\:grid, .swiper-wrapper, .m-custom-content__wrapper, .enova-tabs__row, .enova-search__grid';
  // Contenedor de la lista de tarjetas: las filas se agrupan dentro de él por su posición vertical
  const rowContainer = (card) => card.closest(ROW_CONTAINER) || (card.closest('.m-product-item') || card).parentElement;
  const EDITORIAL_MAX_COLORS = 4;

  // --- 1. Miniaturas ---------------------------------------------------------

  // Se hace en tres pasadas (escribir, medir, escribir) para no forzar un reflow por tarjeta
  function fitAllSwatches(root = document) {
    const items = Array.from(root.querySelectorAll('div[data-pcard-variant-picker]'))
      .map((picker) => {
        const content = picker.querySelector('.m-product-option--content');
        if (!content) return null;
        const nodes = Array.from(content.children).filter((el) => el.classList.contains('m-product-option--node'));
        const more = content.querySelector('[data-enova-more]');
        const rest = more ? parseInt(more.dataset.rest, 10) || 0 : 0;
        return nodes.length ? { picker, content, nodes, more, rest } : null;
      })
      .filter(Boolean);

    // 1. Todo visible y el "+N" con su texto más largo posible, para medir su ancho máximo.
    //    data-enova-fit desactiva la regla CSS que oculta de la 5ª en adelante mientras carga.
    items.forEach(({ picker, nodes, more, rest }) => {
      picker.setAttribute('data-enova-fit', '');
      nodes.forEach((node) => (node.style.display = ''));
      if (more) {
        setMoreCount(more, nodes.length - 1 + rest);
        more.hidden = false;
      }
    });

    // 2. Medir
    items.forEach((item) => {
      item.available = item.picker.clientWidth;
      item.gap = parseFloat(getComputedStyle(item.content).columnGap) || 0;
      item.widths = item.nodes.map((node) => node.getBoundingClientRect().width);
      item.moreWidth = item.more ? item.more.getBoundingClientRect().width : 0;
    });

    // 3. Mostrar las que entren; si no entran todas, dejar hueco para el "+N"
    items.forEach(({ picker, nodes, more, rest, available, gap, widths, moreWidth }) => {
      if (!available) {
        // Tarjeta oculta (pestaña, carrusel sin montar...): se recalcula cuando se vea
        picker.removeAttribute('data-enova-fit');
        if (more && rest === 0) more.hidden = true;
        return;
      }
      const total = (count) => widths.slice(0, count).reduce((sum, w) => sum + w, 0) + Math.max(count - 1, 0) * gap;

      let visible = nodes.length;
      if (rest > 0 || total(visible) > available) {
        while (visible > 1 && total(visible) + gap + moreWidth > available) visible--;
      }
      // Tarjeta editorial: como mucho 4 colores y el resto en «+N»
      if (picker.closest('.enova-card')) visible = Math.min(visible, EDITORIAL_MAX_COLORS);

      nodes.forEach((node, index) => (node.style.display = index < visible ? '' : 'none'));
      const hidden = nodes.length - visible + rest;
      if (more) {
        if (hidden > 0) setMoreCount(more, hidden);
        else more.hidden = true;
      }
    });
  }

  function setMoreCount(more, count) {
    const label = more.querySelector('.text-base');
    if (label) label.textContent = `+${count}`;
    const tooltip = more.querySelector('.m-tooltip__content');
    if (tooltip) tooltip.textContent = tooltip.textContent.replace(/\d+/, count);
  }

  // --- 2. Títulos alineados por fila -----------------------------------------

  function equalizeRows(cards) {
    const titles = cards.map((card) => card.querySelector('.m-product-card__title'));

    titles.forEach((title) => {
      if (!title) return;
      title.style.marginTop = '';
      title.style.minHeight = '';
    });

    const byContainer = new Map();
    cards.forEach((card, index) => {
      const title = titles[index];
      if (!title || !card.offsetParent) return;
      const container = rowContainer(card);
      const cardRect = card.getBoundingClientRect();
      const titleRect = title.getBoundingClientRect();
      if (!byContainer.has(container)) byContainer.set(container, []);
      byContainer.get(container).push({
        title,
        top: cardRect.top,
        offset: titleRect.top - cardRect.top,
        height: titleRect.height,
        margin: parseFloat(getComputedStyle(title).marginTop) || 0,
      });
    });

    // Una fila = tarjetas del mismo contenedor cuyo borde superior coincide (±2px)
    byContainer.forEach((items) => {
      items.sort((a, b) => a.top - b.top);
      const rows = [];
      items.forEach((item) => {
        const row = rows[rows.length - 1];
        if (row && Math.abs(item.top - row[0].top) <= 2) row.push(item);
        else rows.push([item]);
      });

      rows.forEach((row) => {
        if (row.length < 2) return;
        const maxOffset = Math.max(...row.map((item) => item.offset));
        const maxHeight = Math.max(...row.map((item) => item.height));
        row.forEach((item) => {
          const shift = maxOffset - item.offset;
          if (shift > 0.5) item.title.style.marginTop = `${item.margin + shift}px`;
          if (maxHeight - item.height > 0.5) item.title.style.minHeight = `${maxHeight}px`;
        });
      });
    });
  }

  // --- 3. Vídeo al pasar el ratón --------------------------------------------
  // El vídeo no se descarga hasta el primer hover (preload="none") y solo se muestra cuando ya
  // está reproduciéndose, para que la foto no parpadee mientras carga.

  const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');

  function cardVideo(event) {
    const media = event.target.closest && event.target.closest('.enova-card--hover-video .m-product-card__media');
    if (!media || media.contains(event.relatedTarget)) return null;
    return media.querySelector('[data-enova-card-video]');
  }

  document.addEventListener('mouseover', (event) => {
    if (!canHover.matches) return;
    const video = cardVideo(event);
    if (!video) return;
    if (!video.dataset.enovaBound) {
      video.dataset.enovaBound = 'true';
      video.addEventListener('playing', () => {
        if (video.dataset.enovaHover === 'true') video.parentElement.classList.add('is-playing');
      });
    }
    video.dataset.enovaHover = 'true';
    video.play().catch(() => {});
  });

  document.addEventListener('mouseout', (event) => {
    const video = cardVideo(event);
    if (!video) return;
    video.dataset.enovaHover = 'false';
    video.parentElement.classList.remove('is-playing');
    video.pause();
    video.currentTime = 0;
  });

  // --- 4. Colores hermanos: vista previa al pasar el ratón -------------------
  // Las miniaturas de enova-color-siblings.liquid enlazan a otros productos del mismo modelo.
  // Al pasar por una, la foto de la tarjeta muestra ese color; al salir de la fila vuelve la original.

  const PREVIEW_SWATCH = '[data-enova-siblings] [data-enova-sibling], .enova-card [data-pcard-variant-picker] [data-enova-sibling]';
  const PREVIEW_PICKER = '[data-enova-siblings], .enova-card [data-pcard-variant-picker]';

  function siblingCardImage(picker) {
    if (!picker) return null;
    const card = picker.closest('.m-product-card');
    const img = card && card.querySelector('.m-product-card__main-image img');
    return img ? { card, img } : null;
  }

  // Al entrar el cursor en una tarjeta se precargan (y decodifican) las fotos de sus colores, con
  // el mismo tamaño que pediría la foto de la tarjeta: al pasar por una miniatura, el cambio es
  // inmediato en vez de esperar la descarga.
  const preloaded = new WeakSet();
  function preloadSiblingImages(card) {
    if (preloaded.has(card)) return;
    preloaded.add(card);
    const main = card.querySelector('.m-product-card__main-image img');
    const sizes = (main && main.getAttribute('sizes')) || '';
    card.querySelectorAll('[data-enova-sibling]').forEach((swatch) => {
      if (!swatch.dataset.src) return;
      const img = new Image();
      if (sizes) img.sizes = sizes;
      if (swatch.dataset.srcset) img.srcset = swatch.dataset.srcset;
      img.src = swatch.dataset.src;
      if (img.decode) img.decode().catch(() => {});
    });
  }

  document.addEventListener('pointerover', (event) => {
    if (event.pointerType !== 'mouse') return;
    const card = event.target.closest && event.target.closest('.enova-card');
    if (card && card.querySelector('[data-enova-sibling]')) preloadSiblingImages(card);
  }, { passive: true });

  document.addEventListener('mouseover', (event) => {
    if (!canHover.matches) return;
    // Colores del modelo (productos hermanos) y colores propios del producto (miniaturas del tema
    // con la foto de su galería, snippets/enova-color-preview.liquid)
    const swatch = event.target.closest && event.target.closest(PREVIEW_SWATCH);
    if (!swatch) return;
    const target = siblingCardImage(swatch.closest(PREVIEW_PICKER));
    if (!target) return;
    const { card, img } = target;
    if (!('enovaSrc' in img.dataset)) {
      img.dataset.enovaSrc = img.getAttribute('src') || '';
      img.dataset.enovaSrcset = img.getAttribute('srcset') || '';
    }
    img.setAttribute('srcset', swatch.dataset.srcset);
    img.setAttribute('src', swatch.dataset.src);
    card.classList.add('is-sibling-preview');
  });

  document.addEventListener('mouseout', (event) => {
    const picker = event.target.closest && event.target.closest(PREVIEW_PICKER);
    if (!picker || picker.contains(event.relatedTarget)) return;
    const target = siblingCardImage(picker);
    if (!target || !('enovaSrc' in target.img.dataset)) return;
    const { card, img } = target;
    img.setAttribute('srcset', img.dataset.enovaSrcset);
    img.setAttribute('src', img.dataset.enovaSrc);
    card.classList.remove('is-sibling-preview');
  });

  // En la ficha, un color del propio producto selecciona su variante (la fila Color nativa sigue
  // en el formulario, oculta) en vez de recargar la página.
  document.addEventListener('click', (event) => {
    const swatch = event.target.closest && event.target.closest('.enova-siblings [data-enova-own]');
    if (!swatch || event.metaKey || event.ctrlKey || event.shiftKey) return;
    const picker = swatch.closest('variant-picker');
    const input = picker && Array.from(picker.querySelectorAll('.enova-siblings-replaced input[type="radio"]'))
      .find((radio) => radio.value === swatch.dataset.value);
    if (!input) return;
    event.preventDefault();
    const row = swatch.closest('.enova-siblings');
    row.querySelectorAll('.enova-sibling[data-selected]').forEach((node) => {
      node.removeAttribute('data-selected');
      node.removeAttribute('aria-current');
    });
    swatch.setAttribute('data-selected', 'true');
    swatch.setAttribute('aria-current', 'page');
    const label = row.querySelector('[data-enova-siblings-label]');
    if (label) label.textContent = swatch.dataset.value;
    if (!input.checked) input.click();
  });

  // --- 5. Tarjeta editorial ---------------------------------------------------

  // Primera tarjeta de cada fila (mismo contenedor y mismo borde superior): sin divisor izquierdo.
  function markRowStarts(cards) {
    const byContainer = new Map();
    cards.forEach((card) => {
      if (!card.classList.contains('enova-card')) return;
      if (!card.offsetParent) {
        card.removeAttribute('data-enova-row-start');
        return;
      }
      const container = rowContainer(card);
      if (!byContainer.has(container)) byContainer.set(container, []);
      const rect = card.getBoundingClientRect();
      byContainer.get(container).push({ card, top: rect.top, left: rect.left });
    });
    byContainer.forEach((items) => {
      const firstInRow = new Map();
      items.forEach((item) => {
        const key = Math.round(item.top / 3);
        const current = firstInRow.get(key);
        if (!current || item.left < current.left) firstInRow.set(key, item);
      });
      const starts = new Set(Array.from(firstInRow.values(), (item) => item.card));
      items.forEach(({ card }) => card.toggleAttribute('data-enova-row-start', starts.has(card)));
    });
  }

  // Si ninguna muestra viene elegida (el tema no marca la variante por defecto), se marca la del
  // color que muestra la tarjeta; con un solo color, esa.
  function markCurrentColors(cards) {
    cards.forEach((card) => {
      if (!card.classList.contains('enova-card')) return;
      const labels = Array.from(card.querySelectorAll('[data-pcard-variant-picker] .m-product-option--node__label[data-value]'));
      if (!labels.length) return;
      const selected = labels.some((label) => label.dataset.selected === 'true');
      const line = card.querySelector('[data-enova-card-color]');
      const color = line ? line.dataset.default : '';
      const current = selected ? null : labels.find((label) => label.dataset.value === color) || (labels.length === 1 ? labels[0] : null);
      labels.forEach((label) => label.toggleAttribute('data-enova-current', label === current));
    });
  }

  // El nombre del color sigue a la muestra bajo el cursor; al salir de la fila vuelve al elegido.
  const colorLine = (el) => {
    const card = el.closest && el.closest('.enova-card');
    return card ? card.querySelector('[data-enova-card-color]') : null;
  };

  document.addEventListener('mouseover', (event) => {
    if (!canHover.matches) return;
    const swatch = event.target.closest && event.target.closest('.enova-card [data-pcard-variant-picker] .m-product-option--node__label[data-value]');
    const line = swatch && colorLine(swatch);
    if (line) line.textContent = swatch.dataset.value;
  });

  document.addEventListener('mouseout', (event) => {
    const picker = event.target.closest && event.target.closest('.enova-card [data-pcard-variant-picker]');
    if (!picker || picker.contains(event.relatedTarget)) return;
    const line = colorLine(picker);
    if (line) line.textContent = line.dataset.default;
  });

  // Elegir un color del propio producto (sin cambiar de página) lo deja como el color de la tarjeta.
  document.addEventListener('click', (event) => {
    const swatch = event.target.closest && event.target.closest('.enova-card [data-pcard-variant-picker] label.m-product-option--node__label[data-value]');
    const line = swatch && colorLine(swatch);
    if (!line) return;
    line.dataset.default = swatch.dataset.value;
    line.textContent = swatch.dataset.value;
    const card = swatch.closest('.enova-card');
    card.querySelectorAll('[data-enova-current]').forEach((label) => {
      label.toggleAttribute('data-enova-current', label === swatch);
    });
    // La foto pasa a la de ese color (la de su galería) y queda como la «original» a la que vuelve
    // la vista previa al salir de las miniaturas
    const img = card.querySelector('.m-product-card__main-image img');
    if (img && swatch.dataset.src) {
      if (swatch.dataset.srcset) img.setAttribute('srcset', swatch.dataset.srcset);
      img.setAttribute('src', swatch.dataset.src);
      img.dataset.enovaSrc = swatch.dataset.src;
      img.dataset.enovaSrcset = swatch.dataset.srcset || '';
      card.classList.add('is-sibling-selected');
    }
  });

  // Elegir un color en la tarjeta: toda la tarjeta pasa a ese producto o variante (foto, nombre,
  // color, precio, descuento, enlaces y variante para el carrito), sin salir de la página. Con
  // Cmd/Ctrl/Shift o clic central el enlace abre normal.
  const stripColor = (title, color) => {
    const suffix = ` ${color}`.toLowerCase();
    const clean = title.trim();
    return color && clean.toLowerCase().endsWith(suffix) && clean.length > suffix.length
      ? clean.slice(0, clean.length - suffix.length)
      : clean;
  };

  function selectCardColor(swatch) {
    const card = swatch.closest('.enova-card');
    if (!card) return;
    const data = swatch.dataset;

    card.querySelectorAll('[data-enova-siblings] .enova-sibling[data-selected]').forEach((node) => {
      node.removeAttribute('data-selected');
      node.removeAttribute('aria-current');
    });
    swatch.setAttribute('data-selected', 'true');
    swatch.setAttribute('aria-current', 'true');
    card.querySelectorAll('[data-enova-current]').forEach((label) => label.removeAttribute('data-enova-current'));

    // Foto: queda como la «original» a la que vuelve la vista previa al salir de la fila
    const img = card.querySelector('.m-product-card__main-image img');
    if (img && data.src) {
      if (data.srcset) img.setAttribute('srcset', data.srcset);
      img.setAttribute('src', data.src);
      img.dataset.enovaSrc = data.src;
      img.dataset.enovaSrcset = data.srcset || '';
    }
    // La segunda foto y el vídeo de hover son del producto original
    card.classList.add('is-sibling-selected');

    const color = data.value || '';
    const name = card.querySelector('.m-product-card__name');
    if (name && data.enovaTitle) {
      name.textContent = stripColor(data.enovaTitle, color);
      name.title = data.enovaTitle;
    }
    const line = card.querySelector('[data-enova-card-color]');
    if (line) {
      line.dataset.default = color;
      line.textContent = color;
    }

    const price = card.querySelector('.m-price');
    if (price && data.enovaPrice) {
      const onSale = Boolean(data.enovaCompare);
      price.classList.toggle('m-price--on-sale', onSale);
      price.querySelectorAll('.m-price__regular .m-price-item--regular, .m-price-item--sale').forEach((el) => {
        el.textContent = data.enovaPrice;
      });
      const compare = price.querySelector('.m-price__sale s.m-price-item--regular');
      if (compare) compare.textContent = data.enovaCompare || '';
    }

    const badge = card.querySelector('.m-product-card__tag-discount');
    if (badge) {
      const off = parseInt(data.enovaOff, 10) || 0;
      badge.textContent = off ? `-${off}%` : '';
      badge.classList.toggle('!m:hidden', !off);
    }

    if (data.enovaUrl) {
      card.querySelectorAll('a.m-product-card__link, a.m-product-card__name').forEach((link) => {
        link.href = data.enovaUrl;
      });
    }
    const input = card.querySelector('input[name="id"]');
    if (input && data.enovaVariant) input.value = data.enovaVariant;
  }

  // «+N» de las muestras en la tarjeta editorial: lleva a la ficha del producto (el del color que
  // muestra la tarjeta), en vez de abrir la vista rápida del tema. Se atiende en la fase de
  // captura para ganarle al manejador del tema. Cmd/Ctrl/Mayús o clic central: pestaña nueva.
  const MORE = '.enova-card .m-product-option--more-option';
  const moreUrl = (more) => {
    const card = more.closest('.enova-card');
    const link = card && card.querySelector('a.m-product-card__link, a.m-product-card__name');
    return (link && link.href) || more.getAttribute('href');
  };
  const openMore = (event, more) => {
    const url = moreUrl(more);
    if (!url) return;
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1) window.open(url, '_blank', 'noopener');
    else window.location.href = url;
  };
  ['click', 'auxclick'].forEach((type) => {
    document.addEventListener(type, (event) => {
      const more = event.target.closest && event.target.closest(MORE);
      if (!more || (type === 'auxclick' && event.button !== 1)) return;
      openMore(event, more);
    }, true);
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const more = event.target.closest && event.target.closest(MORE);
    if (more) openMore(event, more);
  }, true);

  // Accesible como enlace aunque el tema lo arme como <div>
  function markMoreLinks(scope) {
    scope.querySelectorAll(`${MORE}:not(a):not([role])`).forEach((more) => {
      more.setAttribute('role', 'link');
      more.tabIndex = 0;
    });
  }

  document.addEventListener('click', (event) => {
    const swatch = event.target.closest && event.target.closest('.enova-card [data-enova-siblings] a.enova-sibling[data-enova-url]');
    if (!swatch || event.metaKey || event.ctrlKey || event.shiftKey || event.button > 0) return;
    event.preventDefault();
    selectCardColor(swatch);
  });

  // --- Orquestación ----------------------------------------------------------

  let scheduled = false;
  const observed = new WeakSet();
  // La primera notificación de un contenedor recién observado llega justo después de calcularlo:
  // se ignora para no repetir el trabajo
  const justObserved = new WeakSet();
  const resizeObserver = 'ResizeObserver' in window
    ? new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        if (justObserved.has(entry.target)) {
          justObserved.delete(entry.target);
          return;
        }
        scheduleRoot(entry.target);
      });
    })
    : null;

  function run(root) {
    const scope = root instanceof Element ? root : document;
    if (scope === document) scheduled = false;
    fitAllSwatches(scope);

    const cards = Array.from(scope.querySelectorAll(CARD));
    equalizeRows(cards);
    markRowStarts(cards);
    markCurrentColors(cards);
    markMoreLinks(scope);

    if (resizeObserver) {
      cards.forEach((card) => {
        const container = rowContainer(card);
        if (container && !observed.has(container)) {
          observed.add(container);
          justObserved.add(container);
          resizeObserver.observe(container);
        }
      });
    }
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(run);
  }

  // Solo los contenedores donde cambió algo (tarjetas nuevas, un carrusel que cambió de
  // tamaño), no todas las tarjetas de la página: el buscador, por ejemplo, inserta 24 tarjetas
  // en cada búsqueda y recalcular las del home entero trababa su transición.
  const pendingRoots = new Set();
  let rootFrame = 0;
  function scheduleRoot(root) {
    if (!root) return;
    pendingRoots.add(root);
    if (rootFrame) return;
    rootFrame = requestAnimationFrame(() => {
      rootFrame = 0;
      const roots = Array.from(pendingRoots);
      pendingRoots.clear();
      if (scheduled) return;
      roots.forEach((item) => {
        if (item.isConnected) run(item);
      });
    });
  }

  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue;
        const cards = node.matches(CARD) ? [node] : node.querySelectorAll(CARD);
        cards.forEach((card) => scheduleRoot(rowContainer(card)));
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });

  window.addEventListener('resize', schedule);
  // Tarjetas que pasan a verse sin cambiar el DOM (pestañas de enova-product-tabs.js)
  // Solo las tarjetas de ese bloque (no todas las de la página), para no trabar la pestaña
  document.addEventListener('enova:cards-visible', (event) => {
    const root = event.detail && event.detail.root;
    if (!root) {
      schedule();
      return;
    }
    requestAnimationFrame(() => {
      if (!scheduled) run(root);
    });
  });
  window.addEventListener('load', schedule);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(schedule);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }
})();
